import * as React from "react";
import {AppTranslations} from "../../../../components/Utils/translations/AppTranslations";
import {Period, PeriodStatus, SelectedPeriod,} from "../pages/NewCensusProcess/types";
import {EQ_TABLES_NAMES, EQUIPMENT_STATUS} from "../types";
import {JSONCensus} from "../data/models/JSONCensus";
import {DataResponse, RestApiError} from "../data/models/RestApi";
import {CENSUS_COOLER_STATUS_TEXTS} from "../components/CensusCoolerStatusChip";
import {CensusProgressInfo} from "../data/models/CensusProgressInfo";
import {createNewCensus, deleteCensus} from "../data/controllers/CensusCoolerController";
import {DATA_STATE} from "../../../../users/contexts/UsersDashboardContext";
import {uuid} from "../../../../types/veboxModels";
import {getEquipmentStatuses} from "../../MassiveActionsController";
import {JSONCensusAvailabilityCheckResult} from "../data/models/JSONCensusAvailabilityCheckResult";
import {dateToISO} from "../../../../dashboard/operativity/shared";

declare var AppTranslations: AppTranslations;

interface CensusCoolerContextType {
    AppTranslations: AppTranslations;
    handlePeriodChange: (
        period: string,
        startDate?: Date,
        endDate?: Date,
    ) => PeriodStatus;
    selectedPeriod: SelectedPeriod;
    getEquipmentTableHeadersName: (name: EQ_TABLES_NAMES) => string;
    census: JSONCensus;
    createCensus: () => Promise<JSONCensus>;
    isLoading: boolean;
    error: RestApiError;
    getStatusText: (status: CENSUS_COOLER_STATUS_TEXTS) => string;
    updateLastUpdatingInfo: (info: CensusProgressInfo) => void;
    lastUpdatingInfo: CensusProgressInfo;
    censusAvailability: DataResponse<JSONCensusAvailabilityCheckResult>;
    acceptedEquipmentStatuses: { status: DATA_STATE; data: uuid[] };
    fromCensusHistory?: boolean;
    isFromExistingCensus?: boolean;
    deleteCurrentCensusAndStartNew: () => Promise<void>;
}

export const CensusCoolerContext = React.createContext<CensusCoolerContextType>(
    {} as any,
);

type CensusCoolerProps = {
    fromCensusHistory?: boolean;
    isCensusFeatureAvailable?: DataResponse<JSONCensusAvailabilityCheckResult>;
};

const getSelectedPeriod = (
    period: string,
    startDate?: Date,
): SelectedPeriod => {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    const todayString = dateToISO(today);
    switch (period) {
        case Period.yearToDate:
            return {
                period,
                startDate: dateToISO(new Date(today.getFullYear(), 0, 1)),
                endDate: todayString,
            };
        case Period.last12month:
            return {
                period,
                startDate: dateToISO(
                    new Date(today.getFullYear() - 1, today.getMonth(), today.getDate()),
                ),
                endDate: todayString,
            };
        case Period.customDate:
            return {
                period,
                startDate: dateToISO(
                    new Date(
                        (startDate || yesterday).setDate(
                            (startDate || yesterday).getDate(),
                        ),
                    ),
                ),
                endDate: todayString,
            };
        default:
            return undefined;
    }
};

export const CensusCoolerProvider: React.FC<CensusCoolerProps> = ({
                                                                      children,
                                                                      fromCensusHistory,
                                                                      isCensusFeatureAvailable,
                                                                  }) => {
    const [acceptedEquipmentStatuses, setAcceptedEquipmentStatuses] =
        React.useState<{
            status: DATA_STATE;
            data: uuid[];
        }>({status: DATA_STATE.isLoading, data: []});

    const [selectedPeriod, setSelectedPeriod] = React.useState<SelectedPeriod>(
        getSelectedPeriod(null),
    );

    const [census, setCensus] = React.useState<JSONCensus>(
        isCensusFeatureAvailable?.data?.lastActiveUserCensus ?? null);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [isFromExistingCensus, setIsFromExistingCensus] = React.useState(
        !!isCensusFeatureAvailable?.data?.lastActiveUserCensus);
    const [error, setError] = React.useState<RestApiError>();
    const [lastUpdatingInfo, setLastLastUpdatingInfo] =
        React.useState<CensusProgressInfo>(undefined);

    const updateLastUpdatingInfo = (info: CensusProgressInfo) => {
        setLastLastUpdatingInfo(info);
    };

    const deleteCurrentCensus = async (censusGuid: uuid) => {
        try {
            await deleteCensus(censusGuid);
            setCensus(null);
        } catch (error) {
            console.error("Error while deleting census", error);
            setError(error);
        }
    }

    const deleteCurrentCensusAndStartNew = async () => {
        await deleteCurrentCensus(census?.guid)
        setIsFromExistingCensus(false)
    }

    const getEquipmentTableHeadersName = (name: EQ_TABLES_NAMES) => {
        const texts = AppTranslations.massiveActions.censusCooler;
        switch (name) {
            case EQ_TABLES_NAMES.CODE:
                return texts.code;
            case EQ_TABLES_NAMES.NAME:
                return texts.name;
            case EQ_TABLES_NAMES.MODEL:
                return texts.model;
            case EQ_TABLES_NAMES.LOCATION:
                return texts.location;
            case EQ_TABLES_NAMES.SINCE:
                return texts.installationDate;
            case EQ_TABLES_NAMES.REMOVE:
                return "";
            case EQ_TABLES_NAMES.ALL:
                return "All";
            case EQ_TABLES_NAMES.ACTION:
                return texts.action;
            default:
                return "";
        }
    };

    const getStatusText = (status: CENSUS_COOLER_STATUS_TEXTS): string => {
        switch (status) {
            case CENSUS_COOLER_STATUS_TEXTS.IN_PROGRESS:
                return AppTranslations?.readAndWrite.inProgress ?? "In Progress";
            case CENSUS_COOLER_STATUS_TEXTS.COMPLETED:
                return AppTranslations?.readAndWrite.completed ?? "Done";
            default:
                return "None";
        }
    };

    const handlePeriodChange = (
        period: Period,
        startDate?: Date,
    ): PeriodStatus => {
        const selectedPeriod = getSelectedPeriod(period, startDate);
        setSelectedPeriod(selectedPeriod);
        switch (period) {
            case Period.yearToDate:
            case Period.last12month:
                return {
                    status: true,
                    errorMessage: "",
                    selectedPeriod,
                };
            case Period.customDate:
                if (
                    new Date(selectedPeriod.startDate) <= new Date(selectedPeriod.endDate)
                ) {
                    return {
                        status: true,
                        errorMessage: "",
                        selectedPeriod,
                    };
                }
                return {
                    status: false,
                    errorMessage: "Start date must be less than or equal to end date",
                };
            default:
                return null;
        }
    };

    const createCensus = async () => {
        if (census) {
            return census;
        } else {
            try {
                const newCensus = await createNewCensus();
                setCensus(newCensus);
                setIsLoading(false);
                return newCensus;
            } catch (error) {
                console.error("Error while creating new census", error);
                setIsLoading(false);
                setError(error);
                return null;
            }
        }
    };

    React.useEffect(() => {
        getEquipmentStatuses()
            .then((r) => {
                const filteredEquipmentStatuses = r
                    ?.map((status) => status.key)
                    .filter(
                        (status) =>
                            ![
                                EQUIPMENT_STATUS.MISSING_GUID_STR,
                                EQUIPMENT_STATUS.WRITTEN_OFF_STR,
                            ].includes(status as EQUIPMENT_STATUS),
                    );

                setAcceptedEquipmentStatuses({
                    status: DATA_STATE.loaded,
                    data: filteredEquipmentStatuses,
                });
            })
            .catch(() =>
                setAcceptedEquipmentStatuses({status: DATA_STATE.error, data: []}),
            );
    }, []);

    return (
        <CensusCoolerContext.Provider
            value={{
                AppTranslations,
                handlePeriodChange,
                selectedPeriod,
                getEquipmentTableHeadersName,
                census,
                createCensus,
                isLoading,
                error,
                getStatusText,
                updateLastUpdatingInfo,
                lastUpdatingInfo,
                censusAvailability: isCensusFeatureAvailable,
                acceptedEquipmentStatuses,
                fromCensusHistory,
                isFromExistingCensus,
                deleteCurrentCensusAndStartNew
            }}
        >
            {children}
        </CensusCoolerContext.Provider>
    );
};
